import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ShoppingBag, Truck, MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import Header from "@/components/shared/Header";
import MenuItemCard from "@/components/shared/MenuItemCard";
import ItemDetailModal from "@/components/shared/ItemDetailModal";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBrand } from "@/contexts/BrandContext";
import { usePublicMenu, MenuItem } from "@/hooks/usePublicMenu";
import { usePopularItems } from "@/hooks/usePopularItems";
import { useSessionContext } from "@/contexts/SessionContext";
import { usePublicBranches } from "@/hooks/usePublicBranches";
import { usePublicAbout } from "@/hooks/usePublicAbout";
import { Button } from "@/components/ui/button";

export default function LandingHome() {
  const { t, language } = useLanguage();
  const { brand, localizedName, localizedTagline } = useBrand();
  const navigate = useNavigate();
  const { session } = useSessionContext();
  const branchId = session.order.branchId;
  const { menuItems, loading: menuLoading } = usePublicMenu(branchId);
  const { items: popularOnly, loading: popularLoading } = usePopularItems(branchId ? undefined : brand.id);
  const { branches } = usePublicBranches();
  const { about } = usePublicAbout();
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);

  // If branch selected, filter from branch menu; otherwise use restaurant-wide popular items
  const popularItems = branchId ? menuItems.filter((item) => item.popular) : popularOnly;
  const loading = branchId ? menuLoading : popularLoading;

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* ─── HERO ─── */}
      <Header />
      <section className="relative min-h-[70vh] flex flex-col items-center justify-center text-center bg-foreground text-background overflow-hidden px-4">
        <div className="absolute inset-0 bg-gradient-to-b from-foreground via-foreground/95 to-foreground/80" />
        <div className="relative z-10 max-w-3xl mx-auto space-y-6">
          <div className="flex justify-center">
            {brand.logoType === "image" ? (
              <img src={brand.logo} alt={localizedName(language)} className="h-20 w-20 md:h-28 md:w-28 object-contain" />
            ) : (
              <span className="text-7xl block">{brand.logo}</span>
            )}
          </div>
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-black text-background leading-[1.05] tracking-tight">
            {localizedName(language)}
          </h1>
          <p className="mt-4 text-lg md:text-xl text-background/60 max-w-md mx-auto">
            {localizedTagline(language)}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Button
              size="lg"
              className="rounded-full px-8 py-6 gap-2 text-base font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25"
              onClick={() => navigate("/menu")}
            >
              {t("hero.cta")}
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
        <button
          onClick={() => scrollTo("menu-section")}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce text-background/40 hover:text-background/70 transition-colors"
          aria-label="Scroll down"
        >
          <ChevronDown className="w-8 h-8" />
        </button>
      </section>

      {/* ─── MENU HIGHLIGHTS ─── */}
      <section id="menu-section" className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-primary font-bold text-sm uppercase tracking-widest">{t("featured.title")}</span>
            <h2 className="font-heading text-3xl md:text-5xl font-black text-foreground mt-2">
              {language === "ar" ? "الأكثر شعبية" : "Fan Favorites"}
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-4 rounded-full" />
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="rounded-2xl border bg-card animate-pulse aspect-square" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {popularItems.slice(0, 8).map((item) => (
                <MenuItemCard
                  key={item.id}
                  item={item}
                  onClick={setSelectedItem}
                />
              ))}
            </div>
          )}
          <div className="text-center mt-10">
            <Button
              size="lg"
              variant="outline"
              className="rounded-full px-10 font-bold"
              onClick={() => navigate("/menu")}
            >
              {language === "ar" ? "عرض القائمة الكاملة" : "View Full Menu"}
            </Button>
          </div>
        </div>
      </section>

      {/* ─── ABOUT / STORY ─── */}
      <section id="about-section" className="py-20 md:py-28 bg-muted/50">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <span className="text-primary font-bold text-sm uppercase tracking-widest">
            {language === "ar" ? "معلومات عنا" : "About Us"}
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-black text-foreground mt-2">
            {language === "ar" ? "قصتنا" : "Our Story"}
          </h2>
          <div className="w-16 h-1 bg-primary mx-auto mt-4 rounded-full" />
          <div className="mt-8 space-y-6 text-muted-foreground leading-relaxed text-lg">
            {/* Replace with your actual content */}
            <p>
              {language === "ar"
                ? "نحن نقدم تجربة طعام فريدة من نوعها تجمع بين الأصالة والجودة."
                : "We offer a unique dining experience that combines authenticity and quality."}
            </p>
            <p className="italic text-foreground/70">
              {language === "ar"
                ? '"مهمتنا هي تقديم أفضل المأكولات بأجود المكونات لعملائنا الكرام."'
                : '"Our mission is to provide the best cuisine with the finest ingredients to our valued customers."'}
            </p>
          </div>
        </div>
      </section>

      {/* ─── BRANCHES / CONTACT ─── */}
      <section id="contact-section" className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-primary font-bold text-sm uppercase tracking-widest">
              {language === "ar" ? "فروعنا" : "Our Locations"}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-black text-foreground mt-2">
              {language === "ar" ? "زورونا" : "Visit Us"}
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-4 rounded-full" />
          </div>
          {branches.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {branches.map((branch) => (
                <div
                  key={branch.id}
                  className="rounded-2xl border border-border bg-card p-6 space-y-3 hover:shadow-lg transition-shadow"
                >
                  <h3 className="font-heading font-bold text-lg text-foreground">
                    {language === "ar" ? branch.nameAr || branch.name : branch.name}
                  </h3>
                  {(language === "ar" ? branch.addressAr || branch.address : branch.address) && (
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                      <span>{language === "ar" ? branch.addressAr || branch.address : branch.address}</span>
                    </div>
                  )}
                  {branch.phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="w-4 h-4 shrink-0 text-primary" />
                      <a href={`tel:${branch.phone}`} className="hover:text-primary transition-colors">{branch.phone}</a>
                    </div>
                  )}
                  {(language === "ar" ? branch.workingHoursAr || branch.workingHours : branch.workingHours) && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4 shrink-0 text-primary" />
                      <span>{language === "ar" ? branch.workingHoursAr || branch.workingHours : branch.workingHours}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground">
              {about?.contactPhone || about?.contactEmail ? "" : (language === "ar" ? "لا توجد فروع حالياً" : "No branches listed yet.")}
            </p>
          )}

          {(about?.contactPhone || about?.contactEmail) && (
            <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-muted-foreground">
              {about.contactPhone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary" />
                  <a href={`tel:${about.contactPhone}`} className="hover:text-primary transition-colors">{about.contactPhone}</a>
                </div>
              )}
              {about.contactEmail && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  <a href={`mailto:${about.contactEmail}`} className="hover:text-primary transition-colors">{about.contactEmail}</a>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ─── FOOTER STRIP ─── */}
      <footer className="border-t border-border bg-card py-8">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            {brand.logoType === "image" ? (
              <img src={brand.logo} alt="" className="h-6 w-6 object-contain" />
            ) : (
              <span className="text-lg">{brand.logo}</span>
            )}
            <span className="font-heading font-bold text-foreground">{localizedName(language)}</span>
          </div>
          <p>© {new Date().getFullYear()} {localizedName(language)}. {language === "ar" ? "جميع الحقوق محفوظة" : "All rights reserved."}</p>
        </div>
      </footer>

      {selectedItem && (
        <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} />
      )}
    </div>
  );
}
